import type { IsolationController } from 'features/knowledgeDocumentViewer/graph/IsolationControllerContext'
import type {
  NodeFocusRequest,
  PendingNodeFocus,
} from 'features/knowledgeDocumentViewer/graph/NodeFocusRequestContext'
import type {
  DocumentHistoryItem,
  LotoPanelState,
} from 'features/knowledgeDocumentViewer/types'
import { useCallback, useMemo } from 'react'

import type { LotoOtherPage } from './lotoIsolationPoints'
import type { LotoPageTarget } from './lotoPageLookups'
import {
  type LotoTabOpen,
  type LotoTabUpdate,
  resolveLotoPageNavigation,
} from './lotoPageNavigation'
import type { LotoReportScope } from './lotoReportSchemas'
import {
  useLotoPanelSession,
  type UseLotoPanelSessionResult,
} from './useLotoPanelSession'
import { useLotoIsolationController } from './useLotoSession'

export type OpenLotoPage = (
  page: LotoOtherPage,
  focusNode?: NodeFocusRequest
) => void

export type FocusLotoPoint = (
  focusNode: NodeFocusRequest,
  page?: LotoOtherPage
) => void

export type VisitLotoPage = (target: LotoPageTarget) => void

export type LotoDrawing = {
  controller: IsolationController | undefined
  pendingFocus: PendingNodeFocus | undefined
}

type UseLotoDocumentTabArgs = {
  scope: LotoReportScope | undefined
  panel: LotoPanelState | undefined
  systemDocumentId: string | undefined
  documentId: string
  updateTab: (update: LotoTabUpdate) => void
  addTab: (tab: LotoTabOpen) => void
  pushTab: (item: DocumentHistoryItem) => void
}

function usePendingLotoFocus(
  panel: LotoPanelState | undefined,
  setPanel: (next: LotoPanelState) => void
): PendingNodeFocus | undefined {
  const focusNode = panel?.focusNode
  const activeReportId = panel?.activeReportId
  return useMemo(
    () =>
      focusNode && {
        request: focusNode,
        onHandled: () => setPanel({ activeReportId }),
      },
    [activeReportId, focusNode, setPanel]
  )
}

export function useLotoDocumentTab({
  scope,
  panel,
  systemDocumentId,
  documentId,
  updateTab,
  addTab,
  pushTab,
}: UseLotoDocumentTabArgs): UseLotoPanelSessionResult & {
  openPage: OpenLotoPage
  visitPage: VisitLotoPage
  focusPoint: FocusLotoPoint
  drawing: LotoDrawing
} {
  const activeReportId = panel?.activeReportId
  const setPanel = useCallback(
    (next: LotoPanelState | undefined) => updateTab({ lotoPanel: next }),
    [updateTab]
  )
  const panelSession = useLotoPanelSession(scope, activeReportId, setPanel)
  const { requestLeave } = panelSession.leavePrompt
  const openPage = useCallback<OpenLotoPage>(
    (page, focusNode) => {
      const navigation = resolveLotoPageNavigation(page, {
        activeReportId,
        systemDocumentId,
        focusNode,
      })
      if (navigation.kind === 'sameDocument')
        return updateTab(navigation.update)
      if (navigation.kind === 'otherDocument')
        requestLeave(() => {
          setPanel(undefined)
          addTab(navigation.tab)
        })
    },
    [
      activeReportId,
      addTab,
      requestLeave,
      setPanel,
      systemDocumentId,
      updateTab,
    ]
  )
  const visitPage = useCallback<VisitLotoPage>(
    (target) => {
      if (!activeReportId) return
      const lotoPanel = { activeReportId }
      if (target.documentId === documentId)
        return updateTab({
          pageNumber: target.pageNumber,
          selectedBoxIds: [],
          lotoPanel,
        })
      pushTab({
        type: 'document',
        ...target,
        selectedBoxIds: [],
        query: '',
        lotoPanel,
      })
    },
    [activeReportId, documentId, pushTab, updateTab]
  )
  const focusPoint = useCallback<FocusLotoPoint>(
    (focusNode, page) => {
      if (page) return openPage(page, focusNode)
      setPanel({ activeReportId, focusNode })
    },
    [activeReportId, openPage, setPanel]
  )
  const pendingFocus = usePendingLotoFocus(panel, setPanel)
  const controller = useLotoIsolationController(
    panelSession.session,
    systemDocumentId
  )
  const drawing = useMemo<LotoDrawing>(
    () => ({ controller, pendingFocus }),
    [controller, pendingFocus]
  )
  return { ...panelSession, openPage, visitPage, focusPoint, drawing }
}
